import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BadgeTier from "../components/BadgeTier";
import StarRating from "../components/StarRating";
import VerificationBadges from "../components/VerificationBadges";
import HOFBadges from "../components/HOFBadges";
import AcademyBadges from "../components/AcademyBadges";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDraft } from "../lib/useDraft";
import { DraftBar } from "../components/DraftBar";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { formatApiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Users, Zap, Award, BadgeCheck, Instagram, Youtube, Twitter, Flame, Trophy, ShieldCheck, Plus, Image as ImageIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import BackButton from "../components/BackButton";
import SEO from "../components/SEO";

function toAbsolute(url) {
  if (!url) return url;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}
function isVideo(url = "") {
  return /\.(mp4|webm|mov)$/i.test(url);
}

const FALLBACK_COVER = "https://images.unsplash.com/photo-1727334291061-fd29582ef9dc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

/** Return the full URL for a social handle — accepts either a full https URL
 * (new behaviour) or a bare username / @username (legacy). */
function socialUrl(value, platform) {
  const v = (value || "").trim();
  if (!v) return "#";
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "");
  const prefixes = {
    instagram: "https://instagram.com/",
    tiktok:    "https://tiktok.com/@",
    youtube:   "https://youtube.com/@",
    x:         "https://x.com/",
  };
  return (prefixes[platform] || "https://") + handle;
}

/** Compact display label for a social entry — pretty @handle for legacy,
 * shortened domain path for full URLs. */
function socialLabel(value) {
  const v = (value || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) {
    try {
      const u = new URL(v);
      const path = u.pathname.replace(/^\/@?/, "").replace(/\/$/, "");
      return path ? `@${path.split("/")[0]}` : u.hostname;
    } catch { return v; }
  }
  return `@${v.replace(/^@/, "")}`;
}

const PLATFORM_ICON = { instagram: Instagram, youtube: Youtube, twitter: Twitter };

function formatFollowers(n = 0) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function CreatorPublicProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ amount: "", title: "Custom collab", delivery_days: 7, brief: "", perf: null, needs_shipping: false });
  const offerDraft = useDraft(user?.role === "brand" ? `offer:${id}` : null, {
    value: offer,
    isEmpty: (v) => !v || (!String(v.amount || "").trim() && !v.brief?.trim() && !v.perf && (v.title === "Custom collab" || !v.title)),
    onRestore: (v) => setOffer((o) => ({ ...o, ...v })),
  });
  const [hiring, setHiring] = useState(false);

  useEffect(() => {
    api.get(`/creators/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Creator not found"));
  }, [id]);

  const hire = async () => {
    if (!user) { navigate("/auth?mode=signup&role=brand"); return; }
    if (user.role !== "brand") { toast.error("Only brands can hire creators"); return; }
    const amt = parseFloat(offer.amount);
    if (!amt || amt <= 0) { toast.error("Enter an offer amount"); return; }
    setHiring(true);
    try {
      const perf = offer.perf && parseFloat(offer.perf.rate) > 0 && parseFloat(offer.perf.cap) > 0
        ? { type: offer.perf.type, rate: parseFloat(offer.perf.rate), pct: !!offer.perf.pct, cap: parseFloat(offer.perf.cap), landing_url: offer.perf.landing_url || "" }
        : null;
      if (offer.perf && !perf) { toast.error("Enter a bonus rate and a max bonus pool"); setHiring(false); return; }
      const { data: h } = await api.post("/hires", {
        creator_id: id,
        amount: amt,
        title: offer.title || "Custom collab",
        delivery_days: parseInt(offer.delivery_days) || 7,
        brief: offer.brief,
        performance: perf,
        needs_shipping: !!offer.needs_shipping,
      });
      toast.success("Offer sent — fund escrow to start");
      offerDraft.clear();
      navigate(`/deals/${h.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setHiring(false);
    }
  };

  if (!data) return (
    <div className="min-h-screen grain"><Navbar /><div className="max-w-6xl mx-auto p-10 animate-pulse text-zinc-500">Loading…</div></div>
  );

  const { user: cu, profile: p, reviews } = data;
  const portfolio = p.portfolio || [];
  const cover = toAbsolute(p.cover_url) || toAbsolute(portfolio[0]) || FALLBACK_COVER;
  const avatar = toAbsolute(p.avatar_url) || toAbsolute(portfolio[0]) || null;
  const isOwner = user?.id === cu?.id;

  return (
    <div className="min-h-screen grain">
      <SEO
        title={`${cu.name} · ${p.tagline || (p.niches || []).join(" · ") || "Creator"}`}
        description={
          `${cu.name} is a ${(p.badge_tier || "bronze")}-tier creator on Nexus. ` +
          `${p.tagline || p.bio || ""} ${p.location ? "Based in " + p.location + ". " : ""}` +
          `${p.completed_jobs || 0} completed collabs · ${(p.star_rating || 0).toFixed(1)}★ rating · ${p.follower_count || 0} followers. ` +
          `Hire ${cu.name} on Nexus with escrow protection.`
        }
        path={`/creators/${cu.id}`}
        image={avatar}
        type="profile"
        keywords={[
          `hire ${cu.name}`,
          ...(p.niches || []).map((n) => `${n} creator`),
          ...(p.platforms || []).map((pl) => `${pl} creator`),
          p.location ? `creators in ${p.location}` : "",
          "book creator",
          "influencer for hire",
        ].filter(Boolean)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": cu.name,
          "description": p.tagline || p.bio || "Creator on Nexus",
          "image": avatar,
          "url": `${window.location.origin}/creators/${cu.id}`,
          "jobTitle": "Content Creator",
          "address": p.location ? { "@type": "PostalAddress", "addressLocality": p.location } : undefined,
          "knowsAbout": p.niches || [],
        }}
      />
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/discover" label="Back to Discover" />
      </div>

      {/* Cover */}
      <div className="relative h-64 md:h-80 overflow-hidden mt-4">
        <img src={cover} alt="" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#060608]/50 to-[#060608]" />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-28 relative">
        {/* Hero card */}
        <div className="glass rounded-3xl p-6 md:p-10 grid md:grid-cols-[220px_1fr] gap-8 items-start" data-testid="creator-profile-hero">
          <div className="relative">
            <div className="h-52 w-52 rounded-2xl overflow-hidden hairline nexus-border-glow">
              <img src={avatar} alt={cu.name} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
              <BadgeTier tier={p.badge_tier} size="lg" />
            </div>
          </div>

          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">{cu.name}</h1>
                  {Object.values(p.verifications || {}).filter(Boolean).length >= 4 && (
                    <BadgeCheck className="h-6 w-6 text-nexus-blue" />
                  )}
                  {(cu.agency_badges || []).includes("iia") && (
                    <a href="https://internationalidolsagency.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-sky-500/15 border border-sky-400/40 text-sky-200 hover:bg-sky-500/25" data-testid="profile-iia-badge" title="Represented by International Idols Agency">
                      <BadgeCheck className="h-3 w-3" /> IIA Talent
                    </a>
                  )}
                </div>
                <p className="text-zinc-300 max-w-xl">{p.tagline || p.bio || "Creator on Nexus."}</p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Reputation</div>
                <div className="font-display font-black text-4xl nexus-gradient-text leading-none">{p.reputation_score || 0}</div>
                {p.authenticity_score !== undefined && (
                  <div
                    className={`mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                      p.authenticity_flag === "clean"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : p.authenticity_flag === "suspicious"
                        ? "border-nexus-pink/30 bg-nexus-pink/10 text-nexus-pink"
                        : "border-yellow-500/30 bg-yellow-500/10 text-yellow-300"
                    }`}
                    data-testid="authenticity-chip"
                    title={`Audience authenticity score: ${p.authenticity_score}/100`}
                  >
                    <ShieldCheck className="h-3 w-3" /> {p.authenticity_label || "Audience checked"} · {p.authenticity_score}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {(p.niches || []).map((n) => (
                <span key={n} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-zinc-300">{n}</span>
              ))}
            </div>

            {p.hof_badges?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Hall of Fame</div>
                <HOFBadges badges={p.hof_badges} />
              </div>
            )}

            <AcademyBadges userId={p.user_id || cu?.id} compact={false} title="Academy graduate" maxRows={6} />

            <div className="flex flex-wrap items-center gap-5 text-sm text-zinc-300">
              <StarRating value={p.star_rating || 0} count={p.review_count || 0} size="md" />
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-zinc-500" />{formatFollowers(p.follower_count)}</span>
              {p.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-zinc-500" />{p.location}</span>}
              <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-nexus-blue" />{p.completion_rate || 0}% completion</span>
              <span className="flex items-center gap-1.5"><Award className="h-4 w-4 text-nexus-purple" />{p.completed_jobs || 0} jobs</span>
              {p.level > 0 && (
                <span className="flex items-center gap-1.5" data-testid="profile-level"><Trophy className="h-4 w-4 text-[#FFEA00]" />Lv {p.level} · {p.level_name}</span>
              )}
              {p.current_streak > 0 && (
                <span className="flex items-center gap-1.5" data-testid="profile-streak"><Flame className="h-4 w-4 text-nexus-pink" />{p.current_streak}-day streak</span>
              )}
              {(p.languages || []).length > 0 && (
                <span className="flex items-center gap-1.5" data-testid="profile-languages">
                  🌐 <span className="text-zinc-300">{p.languages.join(" · ")}</span>
                </span>
              )}
            </div>

            {(p.platforms || []).length > 0 && (
              <div className="flex gap-2">
                {(p.platforms || []).map((pl) => {
                  const Icon = PLATFORM_ICON[pl];
                  return (
                    <span key={pl} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-zinc-300">
                      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                      {pl}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          {/* Left: bio, portfolio, reviews */}
          <div className="lg:col-span-2 space-y-10">
            {p.bio && (
              <section>
                <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">About</h3>
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{p.bio}</p>
              </section>
            )}

            <section>
              <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Portfolio</h3>
              {portfolio.length === 0 ? (
                isOwner ? (
                  <button
                    onClick={() => navigate("/creator/profile#portfolio")}
                    className="w-full py-16 rounded-2xl border-2 border-dashed border-white/10 hover:border-nexus-pink/40 hover:bg-white/[0.02] transition-all flex flex-col items-center justify-center gap-3 group"
                    data-testid="portfolio-empty-cta"
                  >
                    <div className="h-14 w-14 rounded-full bg-white/[0.03] group-hover:bg-nexus-pink/15 border border-white/10 group-hover:border-nexus-pink/40 flex items-center justify-center transition-all">
                      <Plus className="h-6 w-6 text-zinc-400 group-hover:text-nexus-pink transition-colors" />
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-white">Add your first portfolio piece</div>
                      <div className="text-xs text-zinc-500 mt-1">Show brands what you make — 6+ pieces gets you noticed</div>
                    </div>
                  </button>
                ) : (
                  <div className="w-full py-14 rounded-2xl border border-white/5 bg-white/[0.02] flex flex-col items-center justify-center gap-2" data-testid="portfolio-empty-viewer">
                    <ImageIcon className="h-8 w-8 text-zinc-600" />
                    <div className="text-sm text-zinc-500">This creator hasn&apos;t added portfolio pieces yet.</div>
                  </div>
                )
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {portfolio.map((u, i) => {
                    const abs = toAbsolute(u);
                    return (
                      <div key={i} className="aspect-square rounded-xl overflow-hidden hairline bg-black">
                        {isVideo(u) ? (
                          <video src={abs} className="w-full h-full object-cover" muted playsInline preload="metadata" controls />
                        ) : (
                          <img src={abs} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-xs uppercase tracking-[0.3em] text-zinc-500">Reviews</h3>
                <StarRating value={p.star_rating || 0} count={p.review_count || 0} size="md" />
              </div>

              {p.review_count > 0 && (
                <div className="grid grid-cols-5 gap-3 mb-6">
                  {["quality", "communication", "creativity", "delivery_speed", "professionalism"].map((k) => (
                    <div key={k} className="p-3 rounded-xl bg-[#0C0C10] hairline text-center">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                        {k.replace("_", " ")}
                      </div>
                      <div className="font-display font-bold text-xl text-white">
                        {p.category_ratings?.[k]?.toFixed(1) || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                {reviews.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center text-zinc-400 text-sm">No reviews yet — be the first to hire.</div>
                ) : (
                  reviews.map((r) => (
                    <div key={r.id} className="p-5 rounded-xl bg-[#0C0C10] hairline" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{r.brand_id ? <Link to={`/brands/${r.brand_id}`} className="hover:text-nexus-pink transition-colors" data-testid={`review-brand-link-${r.id}`}>{r.brand_name}</Link> : r.brand_name}{r.deal_type && <span className="text-zinc-500 font-normal text-xs ml-2">{r.deal_type}</span>}</div>
                        <StarRating
                          value={(r.quality + r.communication + r.creativity + r.delivery_speed + r.professionalism) / 5}
                          size="sm"
                        />
                      </div>
                      {r.comment && <p className="text-sm text-zinc-300 whitespace-pre-wrap">{r.comment}</p>}
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          {/* Right: hire offer, social handles, verifications */}
          <aside className="space-y-6">
            {p.accepting_offers === false ? (
              <div className="glass rounded-2xl p-6" data-testid="not-accepting-panel">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Work with {cu.name}</div>
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1.5 mb-3">Not taking new offers right now</div>
                <p className="text-sm text-zinc-400 mb-4">{cu.name.split(" ")[0]}'s books are full at the moment. Discover other creators in {(p.niches || [])[0] || "this niche"} who are open to offers today.</p>
                <Button onClick={() => navigate(`/discover${(p.niches || [])[0] ? `?niche=${encodeURIComponent(p.niches[0])}` : ""}`)} variant="outline" className="rounded-full border-white/15 text-white w-full h-11" data-testid="find-similar-creators-btn">Find similar creators</Button>
              </div>
            ) : (
            <div className="glass rounded-2xl p-6" data-testid="hire-panel">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Work with {cu.name}</div>
              <p className="text-sm text-zinc-400 mb-4">
                Rates are set per collab. Send an offer and {cu.name.split(" ")[0]} will accept or counter.
              </p>
              <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 w-full h-11"
                    data-testid="send-offer-btn"
                  >
                    Send collab offer
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0C0C10] border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Send an offer to {cu.name}</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                      You set the price. {cu.name.split(" ")[0]} accepts or counter-offers before you fund escrow.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Offer amount (GBP)</div>
                      <Input
                        type="number"
                        value={offer.amount}
                        onChange={(e) => setOffer({ ...offer, amount: e.target.value })}
                        placeholder="e.g. 500"
                        className="bg-white/[0.02] border-white/10 text-white"
                        data-testid="offer-amount-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Collab title</div>
                        <Input
                          value={offer.title}
                          onChange={(e) => setOffer({ ...offer, title: e.target.value })}
                          placeholder="e.g. IG Reel"
                          className="bg-white/[0.02] border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Delivery (days)</div>
                        <Input
                          type="number"
                          value={offer.delivery_days}
                          onChange={(e) => setOffer({ ...offer, delivery_days: e.target.value })}
                          className="bg-white/[0.02] border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Brief</div>
                      <Textarea
                        value={offer.brief}
                        onChange={(e) => setOffer({ ...offer, brief: e.target.value })}
                        placeholder="Deliverables, tone, deadline, references…"
                        rows={4}
                        className="bg-white/[0.02] border-white/10 text-white placeholder:text-zinc-600"
                        data-testid="offer-brief-input"
                      />
                    </div>
                    <DraftBar draft={offerDraft} onStartFresh={async () => { await offerDraft.clear(); setOffer({ amount: "", title: "Custom collab", delivery_days: 7, brief: "", perf: null, needs_shipping: false }); }} />
                    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="offer-shipping-block">
                      <input type="checkbox" checked={!!offer.needs_shipping} onChange={(e) => setOffer({ ...offer, needs_shipping: e.target.checked })} className="h-4 w-4 accent-fuchsia-500" data-testid="offer-shipping-toggle" />
                      <span>I'll ship a product to the creator <span className="text-zinc-500">— we'll collect their address and track the parcel</span></span>
                    </label>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-3" data-testid="performance-offer-block">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <div className="text-sm font-semibold text-white">Add a performance bonus</div>
                          <div className="text-[11px] text-zinc-500">Pay extra per sale or per 1,000 views. The bonus pool sits in escrow; unused funds come back to you.</div>
                        </div>
                        <input type="checkbox" checked={!!offer.perf} onChange={(e) => setOffer({ ...offer, perf: e.target.checked ? { type: "per_sale", rate: "", pct: false, cap: "", landing_url: "" } : null })} className="h-4 w-4 accent-fuchsia-500" data-testid="performance-toggle" />
                      </label>
                      {offer.perf && (
                        <div className="grid grid-cols-2 gap-2">
                          <select value={offer.perf.type} onChange={(e) => setOffer({ ...offer, perf: { ...offer.perf, type: e.target.value } })} className="col-span-2 h-10 rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="performance-type-select">
                            <option value="per_sale">Commission per sale</option>
                            <option value="per_1k_views">Bonus per 1,000 views</option>
                          </select>
                          <Input type="number" min="0" step="0.01" value={offer.perf.rate} onChange={(e) => setOffer({ ...offer, perf: { ...offer.perf, rate: e.target.value } })} placeholder={offer.perf.type === "per_sale" ? (offer.perf.pct ? "% of each sale" : "£ per sale") : "£ per 1,000 views"} className="bg-white/[0.02] border-white/10 text-white" data-testid="performance-rate-input" />
                          <Input type="number" min="1" step="1" value={offer.perf.cap} onChange={(e) => setOffer({ ...offer, perf: { ...offer.perf, cap: e.target.value } })} placeholder="Max bonus pool £" className="bg-white/[0.02] border-white/10 text-white" data-testid="performance-cap-input" />
                          {offer.perf.type === "per_sale" && (
                            <label className="col-span-2 flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={offer.perf.pct} onChange={(e) => setOffer({ ...offer, perf: { ...offer.perf, pct: e.target.checked } })} data-testid="performance-pct-toggle" /> Rate is a % of order value</label>
                          )}
                          <Input value={offer.perf.landing_url} onChange={(e) => setOffer({ ...offer, perf: { ...offer.perf, landing_url: e.target.value } })} placeholder="Product / landing page URL for the tracked link" className="col-span-2 bg-white/[0.02] border-white/10 text-white" data-testid="performance-landing-input" />
                        </div>
                      )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 w-full"
                      onClick={hire}
                      disabled={hiring}
                      data-testid="confirm-offer-btn"
                    >
                      {hiring ? "Sending…" : "Send offer"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            )}

            {Array.isArray(data?.affiliate_links) && data.affiliate_links.length > 0 && (
              <div className="glass rounded-2xl p-6" data-testid="shop-my-links">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Shop my links</div>
                <div className="space-y-2">
                  {data.affiliate_links.map((l) => (
                    <a key={l.code} href={l.link} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2 hover:bg-white/[0.06]" data-testid={`shop-link-${l.code}`}>
                      <div className="flex items-center gap-2 min-w-0">{l.brand_logo ? <img src={l.brand_logo} alt="" className="h-7 w-7 rounded-lg object-cover" /> : <div className="h-7 w-7 rounded-lg bg-white/10" />}<div className="min-w-0"><div className="text-sm text-white truncate">{l.brand_name}</div><div className="text-[11px] text-zinc-500 truncate">{l.program}</div></div></div>
                      <span className="text-[10px] font-mono text-zinc-400 shrink-0">code {l.code}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(p.rate_card) && p.rate_card.filter((r) => r && (r.package || r.price)).length > 0 && (
              <div className="glass rounded-2xl p-6" data-testid="rate-card-panel">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Rate card</div>
                <div className="divide-y divide-white/5">
                  {p.rate_card
                    .filter((r) => r && (r.package || r.price))
                    .map((r, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 gap-4">
                        <span className="text-sm text-zinc-200 truncate">{r.package || "Custom package"}</span>
                        <span className="text-sm font-semibold text-white shrink-0">{r.price || "DM to chat"}</span>
                      </div>
                    ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-3">Rates are indicative — final scope agreed per brief.</p>
              </div>
            )}

            {p.social_handles && Object.values(p.social_handles).some((v) => v) && (
              <div className="glass rounded-2xl p-6" data-testid="socials-panel">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Social handles</div>
                <div className="space-y-2 text-sm">
                  {p.social_handles.instagram && (
                    <a href={socialUrl(p.social_handles.instagram, "instagram")} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-200 hover:text-white">
                      <Instagram className="h-4 w-4 text-nexus-pink" /> {socialLabel(p.social_handles.instagram)}
                    </a>
                  )}
                  {p.social_handles.tiktok && (
                    <a href={socialUrl(p.social_handles.tiktok, "tiktok")} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-200 hover:text-white">
                      <span className="h-4 w-4 text-nexus-pink font-bold text-xs flex items-center justify-center">TT</span> {socialLabel(p.social_handles.tiktok)}
                    </a>
                  )}
                  {p.social_handles.youtube && (
                    <a href={socialUrl(p.social_handles.youtube, "youtube")} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-200 hover:text-white">
                      <Youtube className="h-4 w-4 text-nexus-pink" /> {socialLabel(p.social_handles.youtube)}
                    </a>
                  )}
                  {p.social_handles.x && (
                    <a href={socialUrl(p.social_handles.x, "x")} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-200 hover:text-white">
                      <Twitter className="h-4 w-4 text-nexus-pink" /> {socialLabel(p.social_handles.x)}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Verified</div>
              <VerificationBadges verifications={p.verifications} compact />
            </div>
          </aside>
        </div>
        <div className="h-16" />
      </div>
    </div>
  );
}
